import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import LandingShell from '@/components/landing/LandingShell';
import SEO from '@/components/common/SEO';
import Breadcrumbs from '@/components/semantic/Breadcrumbs';
import FaqSection from '@/components/semantic/FaqSection';
import RelatedLinksSection from '@/components/semantic/RelatedLinksSection';
import SemanticFooter from '@/components/semantic/SemanticFooter';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { docsAppDirectoryEntries, docsAppsHubPage } from '@/lib/semantic-content/appDirectory';
import { resolveSemanticLinks } from '@/lib/semantic-content/pages';
import { getBreadcrumbItems } from '@/lib/semantic-content/breadcrumbs';
import { buildSemanticStructuredData } from '@/lib/semantic-content/schema';

const folderOrder = ['AI', 'Development', 'System', 'Office', 'Explore', 'Games', 'Core'];

export default function DocsApps() {
  const [search, setSearch] = useState('');
  const breadcrumbItems = getBreadcrumbItems(docsAppsHubPage);
  const relatedLinks = resolveSemanticLinks(docsAppsHubPage.relatedLinkPaths);
  const totalApps = docsAppDirectoryEntries.length;
  const normalizedSearch = search.trim().toLowerCase();

  const filteredEntries = useMemo(() => {
    if (!normalizedSearch) {
      return docsAppDirectoryEntries;
    }

    return docsAppDirectoryEntries.filter((entry) => {
      const haystack = [entry.label, entry.description, entry.folder, entry.slug].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [normalizedSearch]);

  const groupedEntries = useMemo(() => {
    const groups = new Map<string, typeof filteredEntries>();

    filteredEntries.forEach((entry) => {
      const existing = groups.get(entry.folder) || [];
      existing.push(entry);
      groups.set(entry.folder, existing);
    });

    return folderOrder
      .map((folder) => ({
        folder,
        entries: (groups.get(folder) || []).sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .filter((group) => group.entries.length > 0);
  }, [filteredEntries]);

  return (
    <LandingShell>
      <SEO
        title={docsAppsHubPage.title}
        description={docsAppsHubPage.description}
        ogTitle={docsAppsHubPage.ogTitle}
        ogDescription={docsAppsHubPage.ogDescription}
        url={docsAppsHubPage.path}
        type="article"
        structuredData={buildSemanticStructuredData(docsAppsHubPage)}

      />

      <div className="bg-background text-foreground">
        <main className="mx-auto flex w-full max-w-7xl flex-col gap-10 px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
          <article className="space-y-10">
            <header className="overflow-hidden rounded-[32px] border border-border/60 bg-card/60 shadow-[0_28px_90px_-50px_rgba(0,0,0,0.75)]">
              <div className="grid gap-8 px-6 py-8 md:px-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-end">
                <div className="space-y-6">
                  <Breadcrumbs items={breadcrumbItems} />
                  <div className="space-y-4">
                    <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary">
                      {docsAppsHubPage.eyebrow}
                    </Badge>
                    <div className="space-y-4">
                      <h1 className="max-w-4xl text-4xl font-semibold tracking-tight md:text-5xl">MapleOS Apps</h1>
                      <p className="max-w-3xl text-lg leading-8 text-muted-foreground">
                        Browse our MapleOS catalog by app name, icon, and role.
                      </p>
                    </div>
                  </div>
                </div>

                <Card className="border-primary/20 bg-background/80 shadow-[0_20px_60px_-36px_rgba(59,130,246,0.6)]">
                  <CardHeader className="space-y-3">
                    <CardTitle className="text-xl">Find apps fast</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search MapleOS apps"
                        className="h-12 rounded-2xl border-primary/20 bg-background/80 pl-11"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </header>

            <section aria-labelledby="apps-directory-heading" className="space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                  <h2 id="apps-directory-heading" className="text-3xl font-semibold tracking-tight text-foreground">
                    Explore the MapleOS app catalog
                  </h2>
                  <p className="mt-2 max-w-3xl text-base leading-7 text-muted-foreground">
                    Click the card to learn more.
                  </p>
                </div>
                {normalizedSearch ? (
                  <p className="text-sm text-muted-foreground">
                    {filteredEntries.length} result{filteredEntries.length === 1 ? '' : 's'} for "{search.trim()}"
                  </p>
                ) : null}
              </div>

              {groupedEntries.length > 0 ? (
                <div className="space-y-8">
                  {groupedEntries.map((group) => (
                    <section key={group.folder} aria-labelledby={`apps-group-${group.folder.toLowerCase()}`} className="space-y-4">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <h3 id={`apps-group-${group.folder.toLowerCase()}`} className="text-2xl font-semibold text-foreground">
                            {group.folder}
                          </h3>
                          <p className="mt-1 text-sm leading-6 text-muted-foreground">{group.entries.length} app{group.entries.length === 1 ? '' : 's'} in this section.</p>
                        </div>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {group.entries.map((entry) => {
                          return (
                            <Link key={entry.path} to={entry.path} className="group block">
                              <Card className="h-full border-border/60 bg-card/60 shadow-none transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:bg-primary/5">
                                <CardHeader className="space-y-4">
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-center gap-4">
                                      <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-border/60 bg-background/80 p-2">
                                        <img
                                          src={`/os/assets/icons/${entry.iconAsset}.svg`}
                                          alt={`${entry.label} icon`}
                                          width={36}
                                          height={36}
                                          loading="lazy"
                                          className="h-9 w-9 object-contain drop-shadow-[0_10px_16px_rgba(0,0,0,0.28)]"
                                        />
                                      </div>
                                      <div>
                                        <CardTitle className="text-xl">{entry.label}</CardTitle>
                                        <p className="mt-1 text-sm text-muted-foreground">{entry.folder}</p>
                                      </div>
                                    </div>
                                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                                  </div>
                                </CardHeader>
                                <CardContent>
                                  <p className="text-sm leading-7 text-muted-foreground">{entry.description}</p>
                                </CardContent>
                              </Card>
                            </Link>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <Card className="border-border/60 bg-card/60 shadow-none">
                  <CardContent className="px-6 py-10 text-center">
                    <p className="text-lg font-medium text-foreground">No MapleOS apps matched "{search.trim()}".</p>
                    <p className="mt-2 text-sm text-muted-foreground">Try a search like AI Center, Music, Code, Office, or Games.</p>
                  </CardContent>
                </Card>
              )}
            </section>

            <FaqSection items={docsAppsHubPage.faq} />

            <RelatedLinksSection
              title="Related MapleOS pages"
              description="Use these pages to connect the app catalog to MapleOS explainers, workflows, and audience pages."
              links={relatedLinks}
            />
          </article>
        </main>
        <SemanticFooter />
      </div>
    </LandingShell>
  );
}